import React, { useState } from 'react';
import styled from 'styled-components';
import { ProductSelected, ProductSelect } from 'components/Select/ProductSelect';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { Modal } from 'components/Modal';
import { ProductState, ProductFlow } from 'data/__generated__';

interface AddProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddProduct: (product: ProductSelected, quantity: number, quantityPicked: number | null) => void;
    boxFlow?: ProductFlow;
}

export const AddProductModal: React.FC<AddProductModalProps> = ({ isOpen, onClose, onAddProduct, boxFlow }) => {
    const [selectedProduct, setSelectedProduct] = useState<ProductSelected | null>(null);
    const [quantity, setQuantity] = useState<number>(1);
    const [quantityPicked, setQuantityPicked] = useState<number | null>(null);

    const handleAddProduct = () => {
        if (selectedProduct) {
            onAddProduct(selectedProduct, quantity, quantityPicked);
            setSelectedProduct(null);
            setQuantity(1);
            setQuantityPicked(null);
            onClose();
        }
    };

    const handleClose = () => {
        setSelectedProduct(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Ajouter un produit à la caisse">
            <ModalContent>
                <ProductSelectWrapper>
                    <ProductSelect
                        value={selectedProduct?._id || null}
                        onChange={setSelectedProduct}
                        filters={{ flow: boxFlow ? [boxFlow] : undefined, states: [ProductState.Regular] }}
                    />
                    {boxFlow && (
                        <FlowFilter>
                            Filtre: uniquement les produits de type "<FlowName>{boxFlow}</FlowName>"
                        </FlowFilter>
                    )}
                </ProductSelectWrapper>

                <QuantityFields>
                    <QuantityField>
                        <Label htmlFor="quantityPicked">Quantité préparée</Label>
                        <Input
                            id="quantityPicked"
                            type="number"
                            min="0"
                            value={quantityPicked === null ? '' : quantityPicked}
                            onChange={(e) => {
                                const value = e.target.value === '' ? null : parseInt(e.target.value);
                                setQuantityPicked(value);
                            }}
                            placeholder="Non préparé"
                        />
                    </QuantityField>
                    <QuantityField>
                        <Label htmlFor="quantity">Quantité requise</Label>
                        <Input
                            id="quantity"
                            type="number"
                            min="0"
                            value={quantity}
                            onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
                        />
                    </QuantityField>
                </QuantityFields>

                <ButtonsContainer>
                    <TotemPrimaryButton onClick={handleAddProduct} disabled={!selectedProduct} $minWidth="200px">
                        Ajouter le produit
                    </TotemPrimaryButton>
                    <TotemPrimaryButton onClick={handleClose} $isSecondaryStyle $minWidth="200px">
                        Annuler
                    </TotemPrimaryButton>
                </ButtonsContainer>
            </ModalContent>
        </Modal>
    );
};

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
`;

const ProductSelectWrapper = styled.div`
    margin-bottom: 20px;
`;

const QuantityFields = styled.div`
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
`;

const QuantityField = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const Label = styled.label`
    font-size: 14px;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.textColor};
`;

const Input = styled.input`
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
    font-size: 14px;

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    gap: 20px;
`;

const FlowFilter = styled.div`
    margin-top: 8px;
    font-size: 14px;
    color: ${({ theme }) => theme.infoTextColor};
    font-style: italic;
`;

const FlowName = styled.span`
    font-weight: 600;
    font-style: normal;
`;
